import React from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';

import clsx from 'clsx';
import { debounce } from 'lodash';
import { Cookies, withCookies } from 'react-cookie';

import { withStyles } from '@material-ui/core/styles';

import { encodeKey } from '_core/utils/storage';
import { getStaticPath } from '_core/utils/getStaticPath';
import { GlobalContext } from '_core/hooks/useGlobal';
import { withContext } from '_core/hocs/withContext';
import { GreetingsSplash } from '_core/views/GreetingsSplash';
import { OrderModal } from '_core/views/OrderModal';
import { PaymentModal } from '_core/views/PaymentModal';
import { ProductModal } from '_core/views/ProductModal';
import { Profile } from '_core/views/Profile';
import { RegisterModal } from '_core/views/RegisterModal';
import { ServiceModal } from '_core/views/ServiceModal';
import { VerificationSplash } from '_core/views/VerificationSplash';
import FloatingWidgets from '_core/components/FloatingWidgets';
import GoodsCartGroupOrderModal from '_core/components/GoodsCartGroupOrderModal';
import { CookieNotice } from '_core/components/CookieNotice';
import { DeliveryCityModal } from '_core/components/DeliveryCityModal';
import { GoodsCartModal } from '_core/components/GoodsCartModal';
import { Header } from '_core/components/Header';
import { UpwardButton } from '_core/components/UpwardButton';

import { Sidebar } from 'basic/views/Sidebar';

import { clientStyle } from './clientStyle';



const RESIZE_DEBOUNCE_DELAY = 200;


class RawClient extends React.PureComponent {
  
  constructor(props) {
    super(props);

    const { cookies, config } = props;
    const { COOKIES_KEY } = config;

    this.state = {
      layoutProps: {},
      mobileOpen: false,
      isMini: cookies.get(encodeKey(COOKIES_KEY.SIDEBAR)) != null,
    };

    this._isMounted = false;
    this.mainPanel = React.createRef();

    this.setProfileTab = this.setProfileTab.bind(this);
    this.sanitizeProfileRoute = this.sanitizeProfileRoute.bind(this);
    this.setSearchParam = this.setSearchParam.bind(this);
    this.setLayoutProps = this.setLayoutProps.bind(this);
    this.minimizeSidebar = this.minimizeSidebar.bind(this);
    this.onDrawerToggle = this.onDrawerToggle.bind(this);

    this._onResize = this._onResize.bind(this);
    this._debouncedOnResize = debounce(this._onResize, RESIZE_DEBOUNCE_DELAY);
  }

  componentDidMount() {
    this._isMounted = true;
    
    this.sanitizeProfileRoute();

    window.addEventListener('resize', this._debouncedOnResize);
  }

  componentWillUnmount() {
    this._isMounted = false;

    this._debouncedOnResize.cancel();
    window.removeEventListener('resize', this._debouncedOnResize);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.history.location.pathname !== prevProps.location.pathname) {
      this.mainPanel.current.scrollTop = 0;

      if (this.state.mobileOpen) {
        this.setState({ mobileOpen: false });
      }
    }
    
    if (prevProps.location.search !== this.props.location.search) {
      this.sanitizeProfileRoute();
    }
  }

  setLayoutProps(layoutProps) {
    this.setState({ layoutProps });
  }

  async minimizeSidebar() {
    const { cookies, config } = this.props;
    const { isMini } = this.state;
    const { COOKIES_TIME, COOKIES_KEY } = config;

    await this.setState({ isMini: !isMini });

    if (!isMini) {
      const stamp = Date.now() + COOKIES_TIME;

      cookies.set(encodeKey(COOKIES_KEY.SIDEBAR), 1, {
        path: '/',
        expires: new Date(stamp)
      });
    } else {
      cookies.remove(encodeKey(COOKIES_KEY.SIDEBAR), {
        path: '/',
        // expires: new Date(stamp),
      });
    }
  }

  onDrawerToggle() {
    this.setState(state => ({
      mobileOpen: !state.mobileOpen,
    }));
  }

  _onResize() {
    if (window.innerWidth >= 960) {
      this.setState({ mobileOpen: false });
    }
  }

  setProfileTab(tab) {
    const { location, history, config } = this.props;
    const { ROUTE_URL_PARAM, PROFILE_TAB_URL, DISABLE_SHOW_PROFILE } = config;
    
    if (DISABLE_SHOW_PROFILE && tab != null)
      return;
    
    const search = new URLSearchParams(location.search);

    if (tab == null) {
      search.delete(ROUTE_URL_PARAM.PROFILE);
    } else {
      search.set(ROUTE_URL_PARAM.PROFILE, tab);
    }

    if (tab !== PROFILE_TAB_URL.VERIFICATION) {
      search.delete(ROUTE_URL_PARAM.VERIFY);
      search.delete(ROUTE_URL_PARAM.CODE);
    }

    history.push({ ...location, search: search.toString() });
  }
  
  sanitizeProfileRoute() {
    const { location, history, config } = this.props;
    const { ROUTE_URL_PARAM, DISABLE_SHOW_PROFILE } = config;
    
    if (!DISABLE_SHOW_PROFILE)
      return;
    
    const search = new URLSearchParams(location.search);
    const hasProfileParams =
      search.has(ROUTE_URL_PARAM.PROFILE) ||
      search.has(ROUTE_URL_PARAM.VERIFY) ||
      search.has(ROUTE_URL_PARAM.CODE);
    
    if (!hasProfileParams)
      return;
    
    search.delete(ROUTE_URL_PARAM.PROFILE);
    search.delete(ROUTE_URL_PARAM.VERIFY);
    search.delete(ROUTE_URL_PARAM.CODE);
    history.replace({ ...location, search: search.toString() });
  }

  setSearchParam(param, value) {
    const { location, history } = this.props;
    const search = new URLSearchParams(location.search);

    if (value == null) {
      search.delete(param);
    } else {
      search.set(param, value);
    }

    location.search = search.toString();
    history.push(location);
  }

  render() {
    const {
      classes,
      location,
      render,
      theme,
      config,
      cookies,
      getSlot,
    } = this.props;

    const { layoutProps, isMini, mobileOpen } = this.state;
    const {
      ROUTE_URL_PARAM,
      PROFILE_TAB_URL,
      COOKIES_KEY,
      HAS_CART,
      HAS_COOKIE,
      VERIFICATION_SPLASH_CAN_POSTPONE,
      SHOW_GREETINGS_SPLASH,
      HIDE_HEADER,
      CustomContentHeader,
      DISABLE_SHOW_PROFILE,
    } = config;
    const { title, menuId, slot, hideCart } = layoutProps;

    const search = new URLSearchParams(location.search);
    const profileTab = search.get(ROUTE_URL_PARAM.PROFILE);
    const paymentId = search.get(ROUTE_URL_PARAM.PAYMENT);
    const documentId = search.get(ROUTE_URL_PARAM.DOCUMENT);
    const shopName = search.get(ROUTE_URL_PARAM.SHOP);
    const serviceId = search.get(ROUTE_URL_PARAM.SERVICE);
    const agreement = search.get(ROUTE_URL_PARAM.AGREEMENT);
    const hasRegister = !!search.get(ROUTE_URL_PARAM.REGISTER);
    

    const slotContent =
      slot != null
        ? getSlot(slot).map((item, index) => React.createElement(item, { key: index }))
        : null;

    const hasVerificationSplash =
      !DISABLE_SHOW_PROFILE &&
      profileTab !== PROFILE_TAB_URL.VERIFICATION &&
      (
        !VERIFICATION_SPLASH_CAN_POSTPONE ||
        cookies.get(encodeKey(COOKIES_KEY.VERIFY)) == null
      );

    const mainClasses = clsx({
      [classes.content]: true,
      [classes.showCart]: HAS_CART && !hideCart,
    });

    const onProfileOpen = DISABLE_SHOW_PROFILE
      ? () => null
      : () => this.setProfileTab(PROFILE_TAB_URL.INFO);

    return (
      <div className={classes.wrapper}>
        {HIDE_HEADER ? null : (
          <Header
            title={title}
            isMini={isMini}
            hideCart={hideCart}
            toggleDrawer={this.onDrawerToggle}
            elevationProps={{ target: this.mainPanel.current }}
            onProfileOpen={onProfileOpen}
          />
        )}

        <Sidebar
          logoText={process.env.REACT_APP_NAME}
          logo={getStaticPath(theme.pages.sidebar.logo)}
          menuId={menuId}
          isOpen={mobileOpen}
          isMini={isMini}
          minimizeSidebar={this.minimizeSidebar}
          toggleDrawer={this.onDrawerToggle}
          onProfileOpen={onProfileOpen}
          hideCart={hideCart}
        />

        <main className={mainClasses} ref={this.mainPanel}>
          {HIDE_HEADER ? null : <div className={classes.headerHeight} />}
          <div id="backToTop" ref={(element) => UpwardButton.scroll.anchor = element} />

          {HIDE_HEADER && CustomContentHeader ? (
            <CustomContentHeader
              title={title}
              toggleDrawer={this.onDrawerToggle}
              onProfileOpen={onProfileOpen}
            />
          ) : null}

          {slotContent}

          {render({
            setLayoutProps: this.setLayoutProps,
          })}
        </main>

        <Profile />
        <OrderModal />
        <ProductModal
          onClose={() => {
            this.setSearchParam(ROUTE_URL_PARAM.SHOP)
          }}
        />

        <ServiceModal
          service={serviceId}
          agreement={agreement}
          onClose={() => {
            search.delete(ROUTE_URL_PARAM.AGREEMENT)
            this.setSearchParam(ROUTE_URL_PARAM.SHOP)
            this.setSearchParam(ROUTE_URL_PARAM.SERVICE)
          }}
        />

        <PaymentModal
          shop={shopName}
          product={paymentId}
          document={documentId}
          onClose={() => {
            this.setSearchParam(ROUTE_URL_PARAM.SHOP);
            this.setSearchParam(ROUTE_URL_PARAM.PAYMENT);
            this.setSearchParam(ROUTE_URL_PARAM.DOCUMENT);
            this.setSearchParam(ROUTE_URL_PARAM.REQUEST);
          }}
        />

        <RegisterModal
          open={hasRegister}
          onClose={() => this.setSearchParam(ROUTE_URL_PARAM.REGISTER)}
        />
        
        {HAS_CART ? <GoodsCartModal /> : null}
        {HAS_CART ? <GoodsCartGroupOrderModal /> : null}
        {hasVerificationSplash ? <VerificationSplash /> : null}
        <GreetingsSplash path={SHOW_GREETINGS_SPLASH} />
        
        <DeliveryCityModal />
        
        <UpwardButton target={this.mainPanel.current} />
        
        <FloatingWidgets />
        
        {HAS_COOKIE ? (
          <CookieNotice />
        ) : null}
      </div>
    );
  }
}

RawClient.propTypes = {
  // self props
  render: PropTypes.func,

  // `withCookies` HOC props
  cookies: PropTypes.instanceOf(Cookies),

  // `GlobalContext` props
  config: PropTypes.object.isRequired,
  request: PropTypes.object.isRequired,

  // `withStyles` HOC props
  theme: PropTypes.object.isRequired,
  classes: PropTypes.object.isRequired
};

export const Client = withContext(GlobalContext)(
  withRouter(
    withCookies(withStyles(clientStyle, { withTheme: true })(RawClient))
  )
);
